// src/components/ErrorBoundary.js
// App-level error boundary so a crash in one page shows a recovery UI
// instead of white-screening the entire application. Because routing is
// state-based, the surrounding Header/Navigation stay mounted, so the user
// can still navigate away (which also remounts and clears this boundary).
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep a record in development; silent in production.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Page render error:', error, info);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        role="alert"
        style={{
          maxWidth: '640px',
          margin: '3rem auto',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid #fecaca',
          background: '#fff5f5',
          textAlign: 'center',
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
        <h2 style={{ color: '#b91c1c', margin: '0 0 0.5rem' }}>
          Something went wrong on this page
        </h2>
        <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>
          Your budget data is safe — it&apos;s stored locally in your browser.
          You can try this page again or switch to another page from the menu
          above.
        </p>

        {process.env.NODE_ENV === 'development' && this.state.error && (
          <pre
            style={{
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              background: '#fee2e2',
              color: '#7f1d1d',
              padding: '0.75rem',
              borderRadius: '8px',
              fontSize: '0.8rem',
              marginBottom: '1.5rem',
              overflowX: 'auto',
            }}
          >
            {String(this.state.error?.stack || this.state.error)}
          </pre>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={this.handleReset}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '0.6rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: 'white',
              color: '#374151',
              padding: '0.6rem 1.5rem',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            Reload App
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
